import { useCallback, useState } from "react";
import { FinancialAccount } from "@common/types/financialAccount";
import { AccountInfoExtraction } from "@common/schemas/financialDocument/account-info-extraction.schema";
import { AccountInfoUpdateBody } from "@api/financialDocument.api";
import { useFinancialAccounts } from "@hooks/useFinancialAccounts";
import { DisabledWithTooltip } from "@design-system/DisabledWithTooltip/DisabledWithTooltip";
import { AccountPickerItem } from "@components/financialAccount/AccountPickerItem";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@design-system/Popover/Popover";
import { Picker } from "@design-system/Picker/Picker.lib";
import {
    ImporterAccountPickerTrigger,
    AccountInfoChangePayload,
} from "./ImporterAccountPickerTrigger";
import styles from "./ImporterPage.module.scss";

interface Props {
    accountInfoExtraction: AccountInfoExtraction | null;
    onAccountInfoChanged: (payload: AccountInfoUpdateBody) => void;
    onFinancialAccountSelected: (financialAccountId: number | null) => void;
    selectedFinancialAccount?: FinancialAccount;
    entityId?: number;
    isAccountLinked?: boolean;
    disabledReason?: string;
}

export const ImporterAccountPicker: React.FC<Props> = ({
    accountInfoExtraction,
    onAccountInfoChanged,
    onFinancialAccountSelected,
    selectedFinancialAccount,
    entityId,
    isAccountLinked = false,
    disabledReason,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const financialAccounts = useFinancialAccounts({
        entityId,
        excludeMockEntities: true,
    });

    const enableNewAccountMode = useCallback(() => {
        if (isAccountLinked) {
            return;
        }
        if (selectedFinancialAccount) {
            onFinancialAccountSelected(null);
        }
        setIsOpen(false);
    }, [isAccountLinked, selectedFinancialAccount, onFinancialAccountSelected]);

    const selectExistingAccount = useCallback(
        (account: FinancialAccount) => {
            if (isAccountLinked) {
                return;
            }
            onFinancialAccountSelected(account.id);
            setIsOpen(false);
        },
        [isAccountLinked, onFinancialAccountSelected],
    );

    const onAccountChanged = useCallback(
        (payload: AccountInfoChangePayload) => {
            onAccountInfoChanged(payload);
        },
        [onAccountInfoChanged],
    );

    return (
        <DisabledWithTooltip reason={disabledReason}>
            <Popover
                open={isOpen}
                onOpenChange={(val) => {
                    if (val && (isAccountLinked || disabledReason)) {
                        return;
                    }
                    setIsOpen(val);
                }}
            >
                <PopoverTrigger>
                    <span className={styles.accountPickerPopoverTrigger}>
                        <ImporterAccountPickerTrigger
                            accountInfoExtraction={accountInfoExtraction}
                            selectedFinancialAccount={selectedFinancialAccount}
                            onAccountChanged={onAccountChanged}
                            readOnly={isAccountLinked}
                        />
                    </span>
                </PopoverTrigger>
                <PopoverContent
                    align="start"
                    className={styles.accountPickerPopover}
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                >
                    <Picker.Select
                        inline
                        classNames={{ popup: styles.accountPickerList }}
                    >
                        <Picker.List className={styles.accountPickerListRows}>
                            <Picker.Item onSelect={enableNewAccountMode}>
                                Create new account
                            </Picker.Item>
                            <Picker.Separator />
                            <Picker.Section>
                                <Picker.SectionTitle>
                                    Use existing account
                                </Picker.SectionTitle>
                                {financialAccounts.map((account) => (
                                    <AccountPickerItem
                                        key={account.id}
                                        account={account}
                                        value={undefined}
                                        onSelect={() =>
                                            selectExistingAccount(account)
                                        }
                                    />
                                ))}
                            </Picker.Section>
                        </Picker.List>
                    </Picker.Select>
                </PopoverContent>
            </Popover>
        </DisabledWithTooltip>
    );
};
